// Mirror of ICMS/src/report/JHS_FINAL_TERM_REPORT_ind.jrxml.
//
// This is the single source of truth for how a Student Report Card
// looks on screen AND when printed. Both StudentReportPage (admin) and
// ExamReportDialog (class-teacher portal) render the fragment through
// reportCardHTML() so the preview matches the paper output byte-for-byte.
//
// Layout, top → bottom (matches the JRXML):
//   1. Page header — school logo + name + motto, student photo top-right
//   2. "Terminal Report" title strip
//   3. Info grid — ADM, Name, Class, Term, Academic Year, Vacation,
//      No On Roll, Repeat/Promoted, Next Term, Class Position, Grade
//   4. "Subjects" band — large left title, right side shows the two
//      scoring percentages (Class Score % + Exam Score %)
//   5. Subject table — 7 columns: Subject | Class Score | Exam Score |
//      Total Score | Grades | Position In Subject | Remark
//   6. Total Score + Average Mark row + TOTAL SUBJECTS counter
//   7. Attendance line (Maximum Required / Actual)
//   8. Conduct + Attitude line
//   9. Class Teacher's Remarks
//  10. Headmaster's Remarks
//  11. Prior-term analysis boxes (t_1 + t_2 subreports) when the current
//      report is Term 2 or 3

export const REPORT_CSS = `
  /* Palette — deep navy #0f2a5c primary, muted gold #c9a24a accent,
     slate #475569 for muted text. Chosen for high print contrast +
     an academic feel without being loud. */

  @page { size: A4 portrait; margin: 0; }
  * { box-sizing: border-box; }

  /* ── Class analysis sheet ─────────────────────────────────────
     A whole class across a page, so it turns LANDSCAPE and sets its
     own width. The at-page rule is document-wide in print CSS, so a
     sheet needing landscape has to be printed on its own — which is
     why the analysis prints from its own button rather than being
     appended to a run of portrait report cards. */
  .an-sheet { width: 297mm; min-height: 210mm; padding: 8mm 9mm; }
  .an-sheet .rc-title { margin-bottom: 2px; }
  .an-tbl { width: 100%; border-collapse: collapse; margin-top: 6px; }
  .an-tbl th, .an-tbl td {
    border: 0.6px solid #b9c2d0; padding: 2px 3px; font-size: 9px;
  }
  .an-tbl thead th {
    background: #0f2a5c; color: #fff; font-weight: 700;
    vertical-align: bottom;
  }
  /* Subject names are long and columns are many — stand them upright
     so 12 subjects still fit across the page instead of wrapping into
     a header three lines deep. */
  .an-tbl th.sub { height: 78px; padding: 2px; }
  .an-tbl th.sub span {
    writing-mode: vertical-rl; transform: rotate(180deg);
    white-space: nowrap; font-size: 8.5px; letter-spacing: .2px;
  }
  .an-tbl td.k, .an-tbl th.k { text-align: center; }
  .an-tbl td.l, .an-tbl th.l { text-align: left; }
  .an-tbl td.l .idx {
    display: block; font-size: 7.5px; color: #64748b; letter-spacing: .2px;
  }
  .an-tbl td.k .sc { display: block; font-size: 7px; color: #64748b; }
  .an-tbl td.dim { color: #94a3b8; }
  .an-tbl td.agg { font-weight: 800; background: #f4f7fb; }
  .an-tbl tbody tr:nth-child(even) td { background: #fbfcfe; }
  .an-tbl tbody tr:nth-child(even) td.agg { background: #eef3fa; }
  .an-foot { display: flex; gap: 10mm; margin-top: 7px; align-items: flex-start; }
  .an-sum { border-collapse: collapse; width: 62mm; }
  .an-sum td { border: 0.6px solid #b9c2d0; padding: 2px 5px; font-size: 9px; }
  .an-sum td.r { text-align: right; }
  .an-note { font-size: 8.5px; color: #475569; max-width: 150mm; line-height: 1.35; }
  .rc-sheet {
    position: relative;
    width: 210mm; min-height: 297mm;
    background: #ffffff; color: #1a1f2b;
    font-family: 'Cambria', 'Georgia', 'Times New Roman', serif;
    padding: 10mm 12mm;
    font-size: 11px; line-height: 1.4;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .rc-watermark {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 55%; max-width: 110mm;
    opacity: 0.04; pointer-events: none; z-index: 0;
  }
  .rc-content { position: relative; z-index: 1; }

  /* 1. Page header ------------------------------------------------ */
  .rc-mast { display: table; width: 100%; margin-bottom: 4px; }
  .rc-mast > div { display: table-cell; vertical-align: middle; }
  .rc-mast-logo   { width: 70px; text-align: left; }
  .rc-mast-logo img { width: 62px; height: 62px; object-fit: contain; }
  .rc-mast-body   { text-align: center; padding: 0 6px; }
  .rc-mast-body .school-name {
    font-weight: 800; font-size: 22px; letter-spacing: 1px;
    text-transform: uppercase; color: #0f2a5c;
  }
  .rc-mast-body .school-motto {
    font-style: italic; font-size: 11px; color: #64748b;
    margin-top: 2px; letter-spacing: 0.3px;
  }
  .rc-mast-body .school-line { font-size: 10.5px; color: #475569; }
  .rc-mast-spacer { width: 70px; }

  /* Gold rule under the masthead — signals the report is a
     "certified" document without an overused divider look. */
  .rc-rule {
    height: 3px; margin: 6px 0 4px;
    background: linear-gradient(90deg, transparent 0%, #c9a24a 15%, #0f2a5c 50%, #c9a24a 85%, transparent 100%);
    border-radius: 2px;
  }

  /* 2. Title strip ------------------------------------------------ */
  .rc-title {
    text-align: center;
    font-family: 'Cambria', 'Georgia', serif;
    font-size: 20px; font-weight: 800; color: #0f2a5c;
    letter-spacing: 3px; text-transform: uppercase;
    padding: 4px 0 2px;
  }
  .rc-title .badge {
    display: inline-block;
    padding: 3px 22px;
    border-top: 1px solid #c9a24a;
    border-bottom: 1px solid #c9a24a;
  }
  .rc-title-sub {
    text-align: center; color: #64748b;
    font-size: 10px; letter-spacing: 3px; text-transform: uppercase;
    margin-top: 2px;
  }

  /* 3. Info grid — refined pill labels instead of grey blocks.
       All cells top-align (align-items: start) so a long value that
       wraps to two lines (e.g. "Promoted → Class 2" or a three-word
       name) doesn't drag its label to the middle of the row and
       throw the whole grid out of alignment. Labels get a small
       top padding so the first character of the value sits on the
       same baseline as the label text. */
  .rc-info {
    display: grid;
    grid-template-columns: 108px 1fr 108px 1fr 108px 1fr;
    align-items: start;
    row-gap: 4px; column-gap: 8px;
    font-size: 10.8px;
    margin: 8px 0 4px;
    padding: 8px 10px;
    background: #f7f9fc;
    border: 1px solid #e3e8f0;
    border-left: 3px solid #c9a24a;
    border-radius: 6px;
  }
  .rc-info .lbl {
    padding: 3px 0 0;
    font-weight: 700; font-size: 9.5px;
    text-transform: uppercase; letter-spacing: 0.4px;
    color: #64748b;
    line-height: 1.25;
  }
  .rc-info .val {
    padding: 2px 4px 3px;
    border-bottom: 1px dotted #b8c1d1;
    font-weight: 600; color: #1a1f2b;
    min-height: 18px;
    line-height: 1.25;
  }
  .rc-info .lbl.strong-lbl { color: #0f2a5c; font-weight: 800; }
  .rc-info .val b { color: #0f2a5c; font-size: 12px; }

  /* 4-5. Subject table (single table — header + body in one grid so
     column widths always line up). The first header cell keeps the
     old "Subjects" banner look via .subjects-banner. */
  .rc-subj {
    width: 100%;
    border-collapse: collapse;
    font-size: 10.8px;
    border: 1px solid #0f2a5c;
    border-radius: 6px 6px 0 0;
    margin-top: 10px;
    overflow: hidden;
    table-layout: fixed;
  }
  .rc-subj thead th {
    background: #eef3fb; color: #0f2a5c;
    font-weight: 700; font-size: 10px;
    text-transform: uppercase; letter-spacing: 0.4px;
    text-align: center; padding: 6px 3px;
    border-left: 1px solid #d6ddea;
    border-bottom: 1px solid #0f2a5c;
    line-height: 1.25;
  }
  .rc-subj thead th:first-child { border-left: none; }
  .rc-subj thead th span {
    display: block; font-weight: 500; font-size: 9.5px;
    color: #64748b; margin-top: 1px;
  }
  .rc-subj thead th.subjects-banner {
    background: linear-gradient(135deg, #0f2a5c 0%, #1c4b9f 100%);
    color: #fff;
    padding: 10px 14px;
    text-align: left;
  }
  .rc-subj thead th.subjects-banner .banner-label {
    font-family: 'Cambria', 'Georgia', serif;
    font-size: 20px; font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: none;
  }
  .rc-subj td {
    padding: 5px 8px;
    border-bottom: 1px solid #e3e8f0;
    border-left: 1px solid #eef3fb;
    vertical-align: middle;
  }
  .rc-subj td:first-child { border-left: none; }
  .rc-subj tbody tr:nth-child(even) td { background: #fbfcfe; }
  .rc-subj .sub-name, .rc-subj tbody tr td:first-child { font-weight: 700; color: #1a1f2b; }
  .rc-subj .center { text-align: center; }
  .rc-subj .right  { text-align: right; }
  .rc-subj tr:last-child td { border-bottom: 1px solid #0f2a5c; }
  .rc-subj .empty {
    padding: 18px; text-align: center;
    color: #64748b; font-style: italic; font-size: 10.5px;
  }

  /* 6. Totals ribbon -------------------------------------------- */
  .rc-tot {
    display: table; width: 100%;
    background: linear-gradient(90deg, #eef3fb 0%, #f7f9fc 100%);
    border-left: 1px solid #0f2a5c;
    border-right: 1px solid #0f2a5c;
    border-bottom: 1px solid #0f2a5c;
    border-radius: 0 0 6px 6px;
  }
  .rc-tot > div {
    display: table-cell; padding: 7px 12px; font-size: 11px;
    border-left: 1px solid #d6ddea;
  }
  .rc-tot > div:first-child { border-left: none; }
  .rc-tot .lbl {
    font-weight: 700; font-size: 9.5px;
    color: #64748b;
    text-transform: uppercase; letter-spacing: 0.5px;
  }
  .rc-tot .val {
    font-weight: 800; color: #0f2a5c;
    font-size: 14px; margin-left: 4px;
  }

  /* 7-8. Attendance + Conduct ----------------------------------- */
  .rc-line {
    display: grid; grid-template-columns: 1fr 1fr;
    column-gap: 22px; row-gap: 6px;
    font-size: 11px; margin-top: 10px;
    padding: 8px 10px;
    background: #fdfcf7;
    border: 1px solid #f0e4a8; border-radius: 6px;
  }
  .rc-line .cell { display: flex; align-items: baseline; gap: 6px; }
  .rc-line .cell .lbl {
    font-weight: 800; white-space: nowrap;
    color: #0f2a5c; font-size: 10.5px;
  }
  .rc-line .cell .val {
    flex: 1; border-bottom: 1px dotted #b8c1d1;
    min-height: 14px; padding-left: 4px;
    color: #1a1f2b; font-weight: 600;
  }

  /* 9-10. Remarks blocks --------------------------------------- */
  .rc-remark { margin-top: 8px; font-size: 11px; }
  .rc-remark .row {
    display: flex; align-items: baseline; gap: 8px;
    margin-top: 4px;
    padding: 6px 10px;
    background: #f7f9fc; border-radius: 4px;
    border-left: 3px solid #c9a24a;
  }
  .rc-remark .row .lbl {
    font-weight: 800; white-space: nowrap; min-width: 170px;
    color: #0f2a5c; font-size: 10.5px;
    text-transform: uppercase; letter-spacing: 0.4px;
  }
  .rc-remark .row .val {
    flex: 1; padding-left: 4px; min-height: 15px;
    color: #1a1f2b; font-style: italic;
  }

  /* 11. Prior-term boxes --------------------------------------- */
  .rc-prior {
    display: grid; grid-template-columns: 1fr 1fr;
    column-gap: 8px; row-gap: 8px; margin-top: 10px;
  }
  .rc-prior .box {
    border: 1px solid #d6ddea;
    border-radius: 6px; overflow: hidden;
    background: #fff;
  }
  .rc-prior .box .title {
    background: linear-gradient(135deg, #0f2a5c 0%, #1c4b9f 100%);
    color: #fff; font-weight: 800; font-size: 9.5px;
    text-transform: uppercase; letter-spacing: 1px;
    padding: 4px 8px;
    display: flex; justify-content: space-between; align-items: center;
    gap: 6px;
  }
  .rc-prior .box .title .subtitle {
    font-weight: 500; font-size: 8.5px; letter-spacing: 0.3px;
    opacity: 0.9; text-transform: none;
  }
  /* Full subject table — mirrors in_t_1.jasper / in_t_2.jasper */
  .rc-prior-tbl {
    width: 100%; border-collapse: collapse;
    table-layout: fixed;
    font-size: 8.6px;
  }
  .rc-prior-tbl thead th {
    background: #eef3fb; color: #0f2a5c;
    font-weight: 700; font-size: 8.2px;
    text-transform: uppercase; letter-spacing: 0.3px;
    padding: 3px 4px; border-bottom: 1px solid #d6ddea;
    text-align: center;
  }
  .rc-prior-tbl thead th:first-child { text-align: left; }
  .rc-prior-tbl tbody td {
    padding: 2px 4px;
    border-bottom: 1px solid #f0f3f8;
    vertical-align: middle;
    color: #1a1f2b;
  }
  .rc-prior-tbl tbody td:first-child { font-weight: 600; }
  .rc-prior-tbl .center { text-align: center; }
  .rc-prior-tbl .num { font-variant-numeric: tabular-nums; }
  .rc-prior-tbl tfoot td {
    padding: 3px 5px;
    background: #f7f9fc;
    border-top: 1px solid #0f2a5c;
    font-weight: 800; color: #0f2a5c;
    font-size: 9px;
  }
  .rc-prior-tbl tfoot .stats {
    text-align: right;
    letter-spacing: 0.2px;
  }

  .page-break { page-break-after: always; break-after: page; }

  @media print {
    html, body { margin: 0; padding: 0; background: #ffffff; }
    .rc-sheet {
      margin: 0 auto; box-shadow: none;
      page-break-after: always;
    }
    .rc-sheet:last-of-type { page-break-after: auto; }
  }
`;

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
  ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

const fmt = (n, d = 0) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString(undefined, {
    minimumFractionDigits: d, maximumFractionDigits: d,
  });
};

// Format an "out of" percentage the way the ICMS report labels the
// column headers (Class Score 50% / Exam Score 100%).
const pct = (out) => {
  const n = Number(out);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `${Math.round(n)}%`;
};

const examTypeLabel = (t) => t === 'mock' ? 'Mock Report'
                          : t === 'mid'  ? 'Mid-Term Report'
                          :                'Terminal Report';

// Prettify Term_1 → "Term 1" for display.
const termLabel = (t) => String(t || '').replace('_', ' ');

// Build the inner HTML for ONE report card.
export function reportCardHTML(data) {
  if (!data) return '';
  const {
    school = {}, student = {}, scope = {},
    subjects = [], totals = {}, conduct = {}, prior_terms = [],
    settings = {},
  } = data;
  const isTerminal = scope.exam_type === 'terminal';
  // Merit settings — driven from Examination Settings → Merit tab.
  // When absent (older backend build) we default to ON so nothing on
  // the existing on-screen preview disappears silently.
  const showPos   = settings.show_position !== false;
  const showGrade = settings.show_grading  !== false;
  const watermark = school.logo
    ? `<img class="rc-watermark" src="${esc(school.logo)}" alt="" />`
    : '';

  // 1) Header — school crest on the left, school block centered.
  // Student photo cell removed per school policy: photos aren't
  // collected any more, so the placeholder just looked awkward on
  // printed sheets. A right-side spacer keeps the school block
  // visually centered against the crest.
  const header = `
    <div class="rc-mast">
      <div class="rc-mast-logo">
        ${school.logo ? `<img src="${esc(school.logo)}" alt="" />` : ''}
      </div>
      <div class="rc-mast-body">
        <div class="school-name">${esc(school.school_name || 'School Name')}</div>
        ${school.motto ? `<div class="school-motto">"${esc(school.motto)}"</div>` : ''}
        <div class="school-line">
          ${school.address ? esc(school.address) : ''}
          ${school.phone   ? ` &middot; Tel: ${esc(school.phone)}` : ''}
        </div>
        ${school.email ? `<div class="school-line">${esc(school.email)}</div>` : ''}
      </div>
      <div class="rc-mast-spacer"></div>
    </div>
  `;

  // 2) Title — gold-rule sandwich, subtitle in muted slate
  const title = `
    <div class="rc-rule"></div>
    <div class="rc-title"><span class="badge">${esc(examTypeLabel(scope.exam_type))}</span></div>
    <div class="rc-title-sub">${esc(termLabel(scope.term))} &middot; ${esc(scope.acc_yer || '')}</div>
  `;

  // 3) Info grid
  const infoBlock = `
    <div class="rc-info">
      <div class="lbl">Adm. No. / ID:</div>
      <div class="val">${esc(student.index_no || '')}</div>
      <div class="lbl">Name:</div>
      <div class="val">${esc(student.fullname || '')}</div>
      <div class="lbl">Class:</div>
      <div class="val">${esc(student.calss || '')}</div>

      <div class="lbl">Term:</div>
      <div class="val">${esc(termLabel(scope.term))}</div>
      <div class="lbl">Academic Year:</div>
      <div class="val">${esc(scope.acc_yer || '')}</div>
      <div class="lbl">Vacation Date:</div>
      <div class="val">${esc(conduct.vacation_date || '')}</div>

      <div class="lbl">No On Roll:</div>
      <div class="val">${esc(conduct.number_on_roll ?? '')}</div>
      <div class="lbl">Repeat/Promoted:</div>
      <div class="val">${esc(conduct.class_status || '')}</div>
      <div class="lbl">Reopening Date:</div>
      <div class="val">${esc(conduct.reopening_date || conduct.next_term || '')}</div>

      ${showPos ? `
        <div class="lbl strong-lbl">Class Position:</div>
        <div class="val"><b>${esc(totals.position || '—')}</b></div>
      ` : `<div></div><div></div>`}
      ${showGrade ? `
        <div class="lbl strong-lbl">Grade:</div>
        <div class="val">${
          (totals.aggregate != null && totals.aggregate !== '')
            ? `<b>${esc(totals.aggregate)}</b>`
            : ''
        }</div>
      ` : `<div></div><div></div>`}
      <div class="lbl"></div>
      <div class="val"></div>
    </div>
  `;

  // 4) Subject band header — big "Subjects" tile + column heads with %.
  // Columns respond to the merit settings: Grades disappears when the
  // school doesn't compute an aggregate; Position In Subject disappears
  // when positions are hidden. When both are off, the remark column
  // stretches to fill the freed space.
  const classPct = pct(subjects[0]?.class_out_of || 50);
  const examPct  = pct(subjects[0]?.exam_out_of  || 100);

  // Build a column spec so the header, sub-header % row and body all
  // stay in sync when columns are toggled off.
  let cols;
  if (isTerminal) {
    cols = [
      { key: 'subject', label: 'Subjects', width: '25%', anchor: 'left'   },
      { key: 'cs',      label: `Class Score${classPct ? `<span>(${classPct})</span>` : ''}`, width: '13.5%', anchor: 'center' },
      { key: 'es',      label: `Exam Score${examPct  ? `<span>(${examPct})</span>`  : ''}`, width: '13.5%', anchor: 'center' },
      { key: 'total',   label: 'Total Score',        width: '11.2%', anchor: 'center' },
    ];
    if (showGrade) cols.push({ key: 'grade',    label: 'Grades',              width: '9%',  anchor: 'center' });
    if (showPos)   cols.push({ key: 'position', label: 'Position In Subject', width: '9%',  anchor: 'center' });
    cols.push({ key: 'remark', label: 'Remark', anchor: 'left' });
  } else {
    cols = [
      { key: 'subject', label: 'Subjects', width: '30%', anchor: 'left'   },
      { key: 'score',   label: 'Score',    width: '14%', anchor: 'center' },
    ];
    if (showGrade) cols.push({ key: 'grade',    label: 'Grade',               width: '10.5%', anchor: 'center' });
    if (showPos)   cols.push({ key: 'position', label: 'Position In Subject', width: '14.5%', anchor: 'center' });
    cols.push({ key: 'remark', label: 'Remark', anchor: 'left' });
  }

  const cellValue = (s, key) => {
    switch (key) {
      case 'subject':  return `<b>${esc(s.subject)}</b>`;
      case 'cs':       return fmt(s.class_score, 0);
      case 'es':       return fmt(s.exam_score, 0);
      case 'total':    return `<b>${fmt(s.overall, 0)}</b>`;
      case 'score':    return fmt(s.score, 0);
      case 'grade':    return esc(s.grade_weight ?? s.grade_letter ?? '');
      case 'position': return esc(s.position || '');
      case 'remark':   return esc(s.grade_remark || '');
      default:         return '';
    }
  };

  // Header + body in ONE table so column widths always line up. The
  // "Subjects" banner used to be a separate LHS box in a two-table
  // layout, but its fixed 160px width didn't match the body's 25%
  // subject column — every data row visually drifted right by ~30px,
  // so the Total value ended up under the Grades header, Grades under
  // Position, and so on. Merging into a single table pins each header
  // cell directly above the value it describes.
  const headerRow = `
    <tr class="rc-subj-head">
      ${cols.map((c, i) => {
        const w = c.width ? ` style="width: ${c.width};"` : '';
        if (i === 0) {
          return `<th class="subjects-banner"${w}><span class="banner-label">Subjects</span></th>`;
        }
        return `<th${w}>${c.label}</th>`;
      }).join('')}
    </tr>
  `;

  const subjectRows = subjects.length === 0
    ? `<tr><td colspan="${cols.length}" class="empty">No subject scores captured for this scope.</td></tr>`
    : subjects.map(s => `
        <tr>
          ${cols.map(c => `<td class="${c.anchor === 'center' ? 'center' : ''}"${c.width ? ` style="width: ${c.width};"` : ''}>${cellValue(s, c.key)}</td>`).join('')}
        </tr>
      `).join('');
  const subjectBand = '';   // headers now live inside the subject table
  const subjectTable = `
    <table class="rc-subj">
      <thead>${headerRow}</thead>
      <tbody>${subjectRows}</tbody>
    </table>
  `;

  // 6) Totals ribbon — three balanced cells with muted labels.
  // The Total Score is derived from the SUM of the ROUNDED subject
  // scores the reader actually sees in the Total Score column above,
  // NOT from the raw stored `totals.total`. This keeps the visible
  // math consistent — e.g. four subjects each showing 100 sum to 400
  // rather than 399 leaking through when the underlying overalls are
  // 99.75 apiece. Average is derived from the same figure.
  const subjectField = isTerminal ? 'overall' : 'score';
  const total = subjects.reduce(
    (sum, s) => sum + Math.round(Number(s[subjectField] || 0)),
    0
  );
  const count = subjects.length;
  const average = count > 0 ? total / count : 0;
  const totalsRow = `
    <div class="rc-tot">
      <div><span class="lbl">Total Subjects</span><span class="val">${count}</span></div>
      <div><span class="lbl">Total Score</span><span class="val">${fmt(total, 0)}</span></div>
      <div><span class="lbl">Average Mark</span><span class="val">${fmt(average, 2)}</span></div>
    </div>
  `;

  // 7-8) Attendance & conduct lines
  const attendanceReq = conduct.attendance_required
    ?? conduct.max_attendance
    ?? '';
  const attendanceActual = conduct.attendance ?? conduct.actual_attendance ?? '';
  const conductLines = `
    <div class="rc-line">
      <div class="cell">
        <span class="lbl">Maximum Attendance Required:</span>
        <span class="val">${esc(attendanceReq)}</span>
      </div>
      <div class="cell">
        <span class="lbl">Actual Attendance Pupil Made:</span>
        <span class="val">${esc(attendanceActual)}</span>
      </div>
      <div class="cell">
        <span class="lbl">Conduct:</span>
        <span class="val">${esc(conduct.conduct || '')}</span>
      </div>
      <div class="cell">
        <span class="lbl">Attitude:</span>
        <span class="val">${esc(conduct.attitude || '')}</span>
      </div>
    </div>
  `;

  // 9-10) Remark blocks
  const remarks = `
    <div class="rc-remark">
      <div class="row">
        <span class="lbl">Class Teacher's Remarks:</span>
        <span class="val">${esc(conduct.teachers_remark || '')}</span>
      </div>
      <div class="row">
        <span class="lbl">Headmaster's Remarks:</span>
        <span class="val">${esc(conduct.headmasters_remark || '')}</span>
      </div>
    </div>
  `;

  // 11) Prior-term analysis. Each box shows only the cells that carry
  // real data: a term with no position stored in the DB won't render
  // an empty "POSITION —" cell. Boxes with nothing meaningful left
  // (e.g. total 0 AND no position AND no aggregate) fall through the
  // backend filter and never reach here. Both toggles also apply, so
  // an "off-position" school never leaks positions in the history block.
  // Prior-term subreports — one mini table per term with the full
  // Subject / Total / Grade / Position / Remark breakdown, echoing the
  // ICMS in_t_1.jasper / in_t_2.jasper subreports. Terms with nothing
  // in them (no subjects, no total, no aggregate) are dropped so the
  // report doesn't end with empty boxes.
  const filteredPrior = (prior_terms || []).filter(p =>
    (Array.isArray(p.subjects) && p.subjects.length > 0) ||
    Number(p.total) > 0 ||
    (p.aggregate != null && p.aggregate !== '') ||
    p.position
  );

  const priorBlock = filteredPrior.length ? `
    <div class="rc-prior">
      ${filteredPrior.map(p => {
        const subs = Array.isArray(p.subjects) ? p.subjects : [];
        const showP = showPos;
        const showG = showGrade;
        const subCount = subs.length;
        // Prior-term totals are derived from the SUM of the ROUNDED
        // subject rows the reader sees, so the math on the strip
        // matches the mini-table above it — same rule the current
        // term uses. Falls back to the stored `p.total` when the
        // prior-term rows aren't available (older imports).
        const derived = subs.reduce(
          (s, r) => s + Math.round(Number(r?.overall ?? r?.score ?? 0)),
          0
        );
        const total = subs.length > 0 ? derived : Number(p.total || 0);
        const avg = subCount > 0 ? total / subCount : 0;
        // Footer strip: subject count · total · average · position.
        // Aggregate ("Aggr N") deliberately dropped — the school's
        // grading scheme conditions live on the CURRENT term only;
        // splashing an aggregate on the prior-term footers created
        // clutter that didn't map to any decision.
        const footerBits = [
          `${subCount} subj`,
          `Total ${fmt(total, 0)}`,
          `Avg ${fmt(avg, 2)}`,
        ];
        if (showP && p.position) footerBits.push(`Pos ${esc(p.position)}`);
        // Header column set matches the current term's compact spec:
        // Subject | Total | Grade | Position | Remark (some cols
        // toggle with merit settings).
        const cols = [
          { key: 'subject',  label: 'Subject',  align: 'left'   },
          { key: 'overall',  label: 'Total',    align: 'center' },
        ];
        if (showG) cols.push({ key: 'grade',    label: 'Grade',    align: 'center' });
        if (showP) cols.push({ key: 'position', label: 'Pos',      align: 'center' });
        cols.push({ key: 'remark', label: 'Remark', align: 'left' });
        const cellFor = (s, k) => {
          switch (k) {
            case 'subject':  return esc(s.subject || '');
            case 'overall':  return `<span class="num">${fmt(s.overall, 0)}</span>`;
            case 'grade':    return esc(s.grade_weight ?? s.grade_letter ?? '');
            case 'position': return esc(s.position || '');
            case 'remark':   return esc(s.grade_remark || '');
            default:         return '';
          }
        };
        const rows = subs.map(s => `
          <tr>${cols.map(c => `<td class="${c.align === 'center' ? 'center' : ''}">${cellFor(s, c.key)}</td>`).join('')}</tr>
        `).join('') || `<tr><td colspan="${cols.length}" style="text-align:center;color:#94a3b8;padding:8px 0;">No subject data captured.</td></tr>`;
        return `
          <div class="box">
            <div class="title">
              <span>${esc(termLabel(p.term))} Result</span>
              <span class="subtitle">${scope.acc_yer ? esc(scope.acc_yer) : ''}</span>
            </div>
            <table class="rc-prior-tbl">
              <thead>
                <tr>${cols.map(c => `<th style="${
                  c.key === 'subject' ? 'width: 38%;' :
                  c.key === 'remark'  ? '' :
                  'width: 15%;'
                }">${c.label}</th>`).join('')}</tr>
              </thead>
              <tbody>${rows}</tbody>
              <tfoot>
                <tr>
                  <td colspan="${cols.length}" class="stats">${footerBits.join(' &middot; ')}</td>
                </tr>
              </tfoot>
            </table>
          </div>`;
      }).join('')}
    </div>
  ` : '';

  // 12) Signatures removed — schools that want a signature just add
  // one by hand on the printed sheet. Keeps every report to one page.

  return `
    <div class="rc-sheet">
      ${watermark}
      <div class="rc-content">
        ${header}
        ${title}
        ${infoBlock}
        ${subjectBand}
        ${subjectTable}
        ${totalsRow}
        ${conductLines}
        ${remarks}
        ${priorBlock}
      </div>
    </div>
  `;
}

// Print-window wrapper. Wraps N report fragments in a printable document.
export function printableDocument(innerHTML, title) {
  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>${REPORT_CSS}</style>
  </head><body>${innerHTML}</body></html>`;
}

// ── Class analysis sheet ────────────────────────────────────────
//
// The report cards answer "how did THIS learner do?", one sheet each.
// This answers the question the head asks the morning after a mock:
// how did the CLASS do, on one page, ranked — the sheet that gets
// pinned up, photocopied for the staff meeting and sent to parents'
// evening. Printing 40 individual cards to read that off them is what
// people were doing instead.
//
// Built entirely from the reports already fetched for the bulk print,
// so a figure here and a figure on a learner's own card come from the
// same payload and cannot disagree.
//
// Ranking: by AGGREGATE first (Ghana — lower is better) when every
// ranked learner has one, otherwise by total score. A learner with no
// aggregate is never ranked above one who has it; they sort to the
// bottom with a dash, because an empty aggregate means the scheme could
// not be filled, not that they did badly.
export function classAnalysisHTML(reports, scope = {}) {
  const list = (reports || []).filter(Boolean);
  if (!list.length) return '<div class="rc-sheet"><p>Nothing to analyse.</p></div>';

  const school = list[0].school || {};
  const isTerminal = scope.exam_type === 'terminal';
  const scoreOf = (s) => Number((isTerminal ? s.overall : s.score) || 0);

  // Every subject that appears anywhere in the class, in the order the
  // first learner sat them, so the columns read the way the timetable
  // does rather than alphabetically.
  const subjectNames = [];
  for (const d of list) {
    for (const s of (d.subjects || [])) {
      if (s.subject && !subjectNames.includes(s.subject)) subjectNames.push(s.subject);
    }
  }

  const rows = list.map(d => {
    const byName = new Map((d.subjects || []).map(s => [s.subject, s]));
    const agg = d.totals?.aggregate;
    const aggNum = Number(agg);
    return {
      name:  d.student?.fullname || d.student?.index_no || '',
      index: d.student?.index_no || '',
      byName,
      total: (d.subjects || []).reduce((sum, s) => sum + Math.round(scoreOf(s)), 0),
      subjectsSat: (d.subjects || []).length,
      aggregate: agg == null || agg === '' ? null : agg,
      aggNum: Number.isFinite(aggNum) ? aggNum : null,
    };
  });

  rows.sort((a, b) => {
    if (a.aggNum != null && b.aggNum != null) return a.aggNum - b.aggNum || b.total - a.total;
    if (a.aggNum != null) return -1;          // has an aggregate → ranks first
    if (b.aggNum != null) return 1;
    return b.total - a.total;
  });

  const head = `
    <tr>
      <th class="k">#</th>
      <th class="l">Name</th>
      ${subjectNames.map(s => `<th class="sub"><span>${esc(s)}</span></th>`).join('')}
      <th class="k">Total</th>
      <th class="k">Agg</th>
    </tr>`;

  const body = rows.map((r, i) => `
    <tr>
      <td class="k">${i + 1}</td>
      <td class="l"><b>${esc(r.name)}</b><span class="idx">${esc(r.index)}</span></td>
      ${subjectNames.map(s => {
        const cell = r.byName.get(s);
        if (!cell) return '<td class="k dim">–</td>';
        // The grade is the point of a mock analysis; the raw score is
        // the tie-breaker, shown small beneath it.
        const g = cell.grade_weight ?? cell.grade_letter ?? '';
        return `<td class="k">${esc(String(g))}<span class="sc">${Math.round(scoreOf(cell))}</span></td>`;
      }).join('')}
      <td class="k"><b>${r.total}</b></td>
      <td class="k agg">${r.aggregate == null ? '–' : esc(String(r.aggregate))}</td>
    </tr>`).join('');

  // Summary — the numbers a head reads out loud.
  const withAgg = rows.filter(r => r.aggNum != null);
  const best    = withAgg.length ? withAgg[0] : null;
  const avgAgg  = withAgg.length
    ? (withAgg.reduce((s, r) => s + r.aggNum, 0) / withAgg.length).toFixed(1) : '–';
  const avgTotal = rows.length
    ? Math.round(rows.reduce((s, r) => s + r.total, 0) / rows.length) : 0;

  const summary = `
    <table class="an-sum">
      <tr><td>Learners</td><td class="r"><b>${rows.length}</b></td></tr>
      <tr><td>Subjects</td><td class="r"><b>${subjectNames.length}</b></td></tr>
      <tr><td>With an aggregate</td><td class="r"><b>${withAgg.length}</b></td></tr>
      <tr><td>Best aggregate</td><td class="r"><b>${best ? esc(String(best.aggregate)) : '–'}</b></td></tr>
      <tr><td>Average aggregate</td><td class="r"><b>${avgAgg}</b></td></tr>
      <tr><td>Average total</td><td class="r"><b>${avgTotal}</b></td></tr>
    </table>`;

  const note = withAgg.length < rows.length
    ? `<div class="an-note">${rows.length - withAgg.length} learner(s) show “–” for the
       aggregate: they did not sit enough graded subjects to fill the school's
       scheme, so no aggregate can honestly be given.</div>`
    : '';

  return `
    <div class="rc-sheet an-sheet">
      <div class="rc-mast">
        <div class="rc-mast-logo">${school.logo ? `<img src="${esc(school.logo)}" alt="" />` : ''}</div>
        <div class="rc-mast-body">
          <div class="school-name">${esc(school.school_name || 'School Name')}</div>
          ${school.motto ? `<div class="school-motto">"${esc(school.motto)}"</div>` : ''}
        </div>
        <div class="rc-mast-spacer"></div>
      </div>
      <div class="rc-rule"></div>
      <div class="rc-title">
        <span class="badge">${esc(examTypeLabel(scope.exam_type).replace(' Report', ''))} Analysis</span>
      </div>
      <div class="rc-title-sub">
        ${esc(scope.cla || '')} &middot; ${esc(termLabel(scope.term))} &middot; ${esc(scope.acc_yer || '')}
        ${scope.exam_num ? ` &middot; No. ${esc(String(scope.exam_num))}` : ''}
      </div>
      <table class="an-tbl"><thead>${head}</thead><tbody>${body}</tbody></table>
      <div class="an-foot">${summary}${note}</div>
    </div>`;
}

export function openPrint(innerHTML, title) {
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) return;
  w.document.write(printableDocument(innerHTML, title));
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 350);
}

export function downloadWord(innerHTML, title, filename) {
  const blob = new Blob([printableDocument(innerHTML, title)], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
